import { Dispatch, SetStateAction, useEffect, useState } from 'react';

import { Alert } from '../base/Alert';
import { pdOrgId } from '../../config';
import { Button } from '../base/Button';
import { Toggle } from '../base/Toggle';
import { Select } from '../base/Select';
import { Form } from '../base/Form/Form';
import { Modal } from '../../layouts/Modal';
import { FormToggle } from '../base/Form/FormToggle';
import { SectionLoading } from '../shared/SectionLoading';
import useUsedSeats from '../../data/billing/useUsedSeats';
import { useUserContext } from '../../contexts/UserContext';
import useSubscription from '../../data/billing/useSubscription';

import { useCalibrationDefaults } from '../../hooks/useCalibrationDefaults';

import { hardwareSettingsFormatters } from '../../utils/hardwareSettingsFormatters';
import { ConfirmSubscriptionUpdate } from '../shared/ConfirmSubscriptionChangeModal';

import {
  IPlanetDeviceUpdateFormFields,
  IPlanetDeviceWithFingotiDevice,
  PlanetDeviceTemperatureProbeLocation,
} from '../../types/PlanetDevice/PlanetDevice';
import usePlanetDeviceMutations from '../../data/planetDevice/usePlanetDeviceMutations';
import { PowerSettings } from './CreateEditDevice/PowerSettings';
import { CurrentTransformerSettings } from './CreateEditDevice/CurrentTransformerSettings';
import { VoltageMeterSettings } from './CreateEditDevice/VoltageMeterSettings';
import { TemperatureSensorSettings } from './CreateEditDevice/TemperatureSensorSettings';
import { SerialSettings } from './CreateEditDevice/SerialSettings';
import { ConfirmDefaultSerialSettings } from './CreateEditDevice/ConfirmDefaultSerialSettings';

export interface CreateEditHardwareProps {
  open: boolean;
  onClose: Dispatch<SetStateAction<boolean>>;
  device?: IPlanetDeviceWithFingotiDevice;
}

export function EditDevice({ open, onClose, device }: CreateEditHardwareProps) {
  const { isLoading: defaultsLoading, getDefaultValue } =
    useCalibrationDefaults();
  const { partnerId, isSuperAdmin } = useUserContext();
  const { updatePlanetDevice, setupPlanetDevice } = usePlanetDeviceMutations();
  const { subscription } = useSubscription();
  const { usedSeats } = useUsedSeats();
  const [subscriptionId, setSubscriptionId] = useState<string | null>(null);
  const [subscriptionTier, setSubscriptionTier] = useState<number>(1);
  const [chosenTier, setChosenTier] = useState<number | null>(null);
  const [subscriptionUpdateOpen, setSubscriptionUpdateOpen] = useState(false);
  const [confirmDefaultSerialOpen, setConfirmDefaultSerialOpen] =
    useState(false);

  const handleClose = () => {
    onClose(false);
    usedSeats.refetch();
    subscription.refetch();
    setTimeout(() => {
      setSubscriptionId(null);
      setSubscriptionTier(1);
      setChosenTier(null);
    }, 200);
  };

  const sendDeviceSetup = (deviceId: string) => {
    setupPlanetDevice.mutate({
      devices: [deviceId],
    });
  };

  const handleUpdate = (data: IPlanetDeviceUpdateFormFields) => {
    updatePlanetDevice.mutate(
      {
        ...device,
        ...data,
        billing: {
          subscriptionId: subscriptionId ? subscriptionId : undefined,
          tier: subscriptionTier!,
        },
        serialSettings: {
          useDefault: data.serialSettings.useDefault,
          baudRate: parseInt(data.serialSettings.baudRate),
          dataBits: parseInt(data.serialSettings.dataBits),
          parity: parseInt(data.serialSettings.parity) as 0 | 1 | 2,
          stopBits: parseInt(data.serialSettings.stopBits),
        },
        powerSettings: {
          measuredVoltageOne: parseInt(data.powerSettings.measuredVoltageOne),
          measuredVoltageTwo: parseInt(data.powerSettings.measuredVoltageTwo),
          measuredVoltageThree: parseInt(
            data.powerSettings.measuredVoltageThree
          ),
          frequency: parseInt(data.powerSettings.frequency),
          phase: parseInt(data.powerSettings.phase),
        },
        peripheralSettings: {
          ctClamp: {
            enabled: data.peripheralSettings.ctClamp.enabled,
            current: parseInt(data.peripheralSettings.ctClamp.current),
            resolution: parseInt(data.peripheralSettings.ctClamp.resolution),
            scale: hardwareSettingsFormatters.ctClampSettings.scale.toInteger(
              data.peripheralSettings.ctClamp.scale
            ),
            offset: hardwareSettingsFormatters.ctClampSettings.offset.toInteger(
              data.peripheralSettings.ctClamp.offset
            ),
          },
          voltmeter: {
            enabled: data.peripheralSettings.voltmeter.enabled,
            scale: hardwareSettingsFormatters.voltMeterSettings.scale.toInteger(
              data.peripheralSettings.voltmeter.scale
            ),
            offset:
              hardwareSettingsFormatters.voltMeterSettings.scale.toInteger(
                data.peripheralSettings.voltmeter.offset
              ),
          },
          temperatureSensor: {
            enabled: data.peripheralSettings.temperatureSensor.enabled,
            location:
              data.peripheralSettings.temperatureSensor.location === 'Outdoor'
                ? PlanetDeviceTemperatureProbeLocation.Outdoor
                : PlanetDeviceTemperatureProbeLocation.Indoor,
            scale:
              hardwareSettingsFormatters.tempSensorSettings.scale.toInteger(
                data.peripheralSettings.temperatureSensor.scale
              ),
            offset:
              hardwareSettingsFormatters.tempSensorSettings.offset.toInteger(
                data.peripheralSettings.temperatureSensor.offset
              ),
          },
        },
      },
      {
        onSuccess: (response) => {
          if (response.data.data[0].id) {
            sendDeviceSetup(response.data.data[0].id);
          }
        },
      }
    );
    handleClose();
  };

  const handleSeatChange = (e: any) => {
    let chosenTier = parseInt(e.target.value);
    let _usedSeats = 0;
    let _subSeats = 0;
    let requireCheck = true;

    switch (chosenTier) {
      case 2:
        _usedSeats = usedSeats.data![0].standard;
        _subSeats = subscription.data![0].seats.standard;
        break;
      case 1:
      default:
        setSubscriptionId(null);
        requireCheck = false;
        break;
    }

    if (requireCheck) {
      if (_usedSeats === _subSeats || _usedSeats > _subSeats) {
        setSubscriptionUpdateOpen(true);
        setChosenTier(chosenTier);
      } else {
        setSubscriptionId(subscription.data![0].id);
        setSubscriptionTier(chosenTier);
      }
    } else {
      setSubscriptionId(null);
      setSubscriptionTier(chosenTier);
    }
  };

  const handleSubscriptionUpdateCancel = () => {
    setSubscriptionUpdateOpen(false);
    setSubscriptionId(null);
    setSubscriptionTier(device?.billing.tier ? device.billing.tier : 1);
  };

  const handleSubscriptionUpdateConfirm = () => {
    setSubscriptionUpdateOpen(false);
    setSubscriptionId(subscription.data![0].id);
    setSubscriptionTier(chosenTier!);
  };

  useEffect(() => {
    if (device) {
      setSubscriptionId(
        device.billing.subscriptionId ? device.billing.subscriptionId : null
      );
      setSubscriptionTier(device.billing.tier);
    } else {
      setSubscriptionId(null);
      setSubscriptionTier(1);
    }
  }, [device]);

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={`${device ? 'Manage' : 'Install'} Device`}
      description={`${device ? 'Manage' : 'Install'} an Atmo device.`}
    >
      {subscription.isLoading || usedSeats.isLoading || defaultsLoading ? (
        <SectionLoading />
      ) : !subscription.isError ? (
        <>
          {subscription.data !== undefined &&
            subscription.data[0] !== undefined && (
              <ConfirmSubscriptionUpdate
                tier="standard"
                currentSeats={subscription.data![0].seats.standard}
                open={subscriptionUpdateOpen}
                onCancel={handleSubscriptionUpdateCancel}
                onConfirm={handleSubscriptionUpdateConfirm}
              />
            )}
          {device?.setupRequired && (
            <Alert
              className="my-6"
              severity="warn"
              title="Pending Changes"
              message="This device has pending changes, please be aware that information displayed below may not reflect the actual state of the device."
            />
          )}
          <Form<IPlanetDeviceUpdateFormFields>
            defaultValues={
              device
                ? {
                    serialSettings: {
                      useDefault: device.serialSettings.useDefault,
                      baudRate: String(device.serialSettings.baudRate),
                      dataBits: String(device.serialSettings.dataBits),
                      parity: String(device.serialSettings.parity),
                      stopBits: String(device.serialSettings.stopBits),
                    },
                    powerSettings: {
                      measuredVoltageOne: device.powerSettings
                        .measuredVoltageOne
                        ? String(device.powerSettings.measuredVoltageOne)
                        : '230',
                      measuredVoltageTwo: device.powerSettings
                        .measuredVoltageTwo
                        ? String(device.powerSettings.measuredVoltageTwo)
                        : '230',
                      measuredVoltageThree: device.powerSettings
                        .measuredVoltageThree
                        ? String(device.powerSettings.measuredVoltageThree)
                        : '230',
                      frequency: device.powerSettings.frequency
                        ? String(device.powerSettings.frequency)
                        : '50',
                      phase: device.powerSettings.phase
                        ? String(device.powerSettings.phase)
                        : '1',
                    },
                    peripheralSettings: {
                      ctClamp: {
                        enabled: device.peripheralSettings.ctClamp?.enabled,
                        current: device.peripheralSettings.ctClamp?.current
                          ? String(device.peripheralSettings.ctClamp?.current)
                          : '30',
                        resolution: device.peripheralSettings.ctClamp
                          ?.resolution
                          ? String(
                              device.peripheralSettings.ctClamp?.resolution
                            )
                          : '1000',
                        offset: device.peripheralSettings.ctClamp?.offset
                          ? hardwareSettingsFormatters.ctClampSettings.offset.toDisplay(
                              device.peripheralSettings.ctClamp?.offset
                            )
                          : getDefaultValue('CtClamp', 'Offset'),
                        scale: device.peripheralSettings.ctClamp?.scale
                          ? hardwareSettingsFormatters.ctClampSettings.scale.toDisplay(
                              device.peripheralSettings.ctClamp?.scale
                            )
                          : getDefaultValue('CtClamp', 'Scale'),
                      },
                      voltmeter: {
                        enabled: device.peripheralSettings.voltmeter?.enabled,
                        offset: device.peripheralSettings.voltmeter?.offset
                          ? hardwareSettingsFormatters.voltMeterSettings.offset.toDisplay(
                              device.peripheralSettings.voltmeter?.offset
                            )
                          : getDefaultValue('Voltmeter', 'Offset'),
                        scale: device.peripheralSettings.voltmeter?.scale
                          ? hardwareSettingsFormatters.voltMeterSettings.scale.toDisplay(
                              device.peripheralSettings.voltmeter?.scale
                            )
                          : getDefaultValue('Voltmeter', 'Scale'),
                      },
                      temperatureSensor: {
                        location:
                          device.peripheralSettings.temperatureSensor
                            .location ===
                          PlanetDeviceTemperatureProbeLocation.Indoor
                            ? 'Indoor'
                            : 'Outdoor',
                        enabled:
                          device.peripheralSettings.temperatureSensor?.enabled,
                        offset: device.peripheralSettings.temperatureSensor
                          ?.offset
                          ? hardwareSettingsFormatters.tempSensorSettings.offset.toDisplay(
                              device.peripheralSettings.temperatureSensor
                                ?.offset
                            )
                          : getDefaultValue('Temperature', 'Offset'),
                        scale: device.peripheralSettings.temperatureSensor
                          ?.scale
                          ? hardwareSettingsFormatters.tempSensorSettings.scale.toDisplay(
                              device.peripheralSettings.temperatureSensor?.scale
                            )
                          : getDefaultValue('Temperature', 'Scale'),
                      },
                    },
                  }
                : {
                    serialSettings: {
                      useDefault: true,
                      baudRate: '0',
                      dataBits: '0',
                      parity: 'default',
                      stopBits: '0',
                    },
                    powerSettings: {
                      measuredVoltageOne: '230',
                      measuredVoltageTwo: '230',
                      measuredVoltageThree: '230',
                      frequency: '50',
                      phase: '1',
                    },
                    peripheralSettings: {
                      ctClamp: {
                        offset: getDefaultValue('CtClamp', 'Offset'),
                        scale: getDefaultValue('CtClamp', 'Scale'),
                        enabled: false,
                        resolution: '1000',
                        current: '30',
                      },
                      voltmeter: {
                        offset: getDefaultValue('Voltmeter', 'Offset'),
                        scale: getDefaultValue('Voltmeter', 'Scale'),
                        enabled: false,
                      },
                      temperatureSensor: {
                        offset: getDefaultValue('Temperature', 'Offset'),
                        scale: getDefaultValue('Temperature', 'Scale'),
                        enabled: false,
                        location: 'Indoor',
                      },
                    },
                  }
            }
            onSubmit={handleUpdate}
            className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-y-4 sm:gap-6 sm:gap-y-4"
          >
            {({ watch, setValue, control }) => (
              <>
                <ConfirmDefaultSerialSettings
                  open={confirmDefaultSerialOpen}
                  onCancel={() => {
                    setConfirmDefaultSerialOpen(false);
                    setValue('serialSettings.useDefault', true);
                  }}
                  onConfirm={() => {
                    setValue('serialSettings.useDefault', false);
                    setConfirmDefaultSerialOpen(false);
                  }}
                />
                {subscription.data !== undefined &&
                subscription.data[0] !== undefined ? (
                  <div className="relative">
                    <Select
                      value={subscriptionTier}
                      onChange={(e) => handleSeatChange(e)}
                      label="Subscription Tier"
                    >
                      <option value={1}>Free</option>
                      <option value={2}>Standard</option>
                    </Select>
                  </div>
                ) : (
                  <>
                    <Select
                      disabled
                      onChange={(e) => handleSeatChange(e)}
                      label="Subscription Tier"
                      color="secondary"
                    >
                      <option value={1}>Free</option>
                      <option value={2}>Standard</option>
                    </Select>
                    {!isSuperAdmin &&
                      (partnerId && partnerId !== pdOrgId ? (
                        <Alert
                          title="Installer managed billing"
                          message="You are unable to make any subscription changes, please contact your installer"
                        />
                      ) : (
                        <Alert
                          severity="error"
                          message="No subscription found, please see the billing section to resolve this"
                        />
                      ))}
                  </>
                )}
                <div
                  className={`flex flex-col space-y-4 items-start sm:space-y-0 sm:space-x-8 sm:my-2 sm:flex-row sm:col-span-2`}
                >
                  <FormToggle<IPlanetDeviceUpdateFormFields>
                    control={control}
                    name="peripheralSettings.ctClamp.enabled"
                    toggleProps={{
                      label: 'Current Transformer',
                      labelRight: true,
                    }}
                  />

                  <FormToggle<IPlanetDeviceUpdateFormFields>
                    control={control}
                    name="peripheralSettings.voltmeter.enabled"
                    toggleProps={{
                      label: 'Voltage Meter',
                      labelRight: true,
                    }}
                  />

                  <FormToggle<IPlanetDeviceUpdateFormFields>
                    control={control}
                    name="peripheralSettings.temperatureSensor.enabled"
                    toggleProps={{
                      label: 'Temperature Sensor',
                      labelRight: true,
                    }}
                  />

                  <FormToggle<IPlanetDeviceUpdateFormFields>
                    control={control}
                    name="serialSettings.useDefault"
                    toggleProps={{
                      label: 'Default Serial Settings',
                      labelRight: true,
                    }}
                  />
                </div>
                <PowerSettings />
                {watch('peripheralSettings.ctClamp.enabled') && (
                  <CurrentTransformerSettings />
                )}
                {watch('peripheralSettings.voltmeter.enabled') && (
                  <VoltageMeterSettings />
                )}
                {watch('peripheralSettings.temperatureSensor.enabled') && (
                  <TemperatureSensorSettings />
                )}
                {!watch('serialSettings.useDefault') && <SerialSettings />}
                <div className="mt-6 sm:col-span-2 space-x-4">
                  <Button type="submit">{device ? 'Update' : 'Create'}</Button>
                  <Button variant="outlined" onClick={handleClose}>
                    Cancel
                  </Button>
                </div>
              </>
            )}
          </Form>
        </>
      ) : (
        <Alert
          severity="error"
          title="Error getting billing information"
          message="We were unable to retrieve billing information, please try again"
        />
      )}
    </Modal>
  );
}
